import { assign, createMachine, fromPromise } from 'xstate'

import Api from '@/api/_legacy'
import {
  ApiAddBusinessUnitUser,
  ApiAddClientUser,
  ApiEditUser,
  UserInformationBasic
} from '@/api/_legacy/generated-api'

import { UserType } from '../../../../types/user'
import { setErrorsAction } from '../../common/set-errors-action'
import { Mutation } from '../../types'
import { EMAIL_REGEX, TENANT_ID } from './utils'

export type EditUserState = {
  errorMessages: string[]
  errors: { [key: string]: string }
  initialUser?: UserInformationBasic
  isSaveEnabled: boolean
  isSaveSuccess: boolean
  notifyParentOfSave?: () => void
  user?: UserInformationBasic
}

type InitEvent = {
  notifyParentOfSave: () => void
  type: 'INIT'
  user: UserInformationBasic
}

type InitAddEvent = {
  notifyParentOfSave: () => void
  type: 'INIT_ADD'
}

type MutateEvent = {
  mutation: Mutation<UserInformationBasic>
  type: 'MUTATE'
}

type SaveEvent = {
  initialUser: UserInformationBasic
  type: 'SAVE'
  user: UserInformationBasic
}

type SaveNewUserEvent = {
  businessUnitId?: string
  clientId?: string
  type: 'SAVE_NEW_USER'
  user: UserInformationBasic
  userType: UserType
}

export type EditUserEvent = InitEvent | InitAddEvent | MutateEvent | SaveEvent | SaveNewUserEvent

export const editUserMachine = createMachine(
  {
    types: {} as {
      context: EditUserState
      events: EditUserEvent
    },
    id: 'edit-user-machine',
    initial: 'idle',
    context: {
      isSaveSuccess: false,
      isSaveEnabled: false,
      user: {
        userId: '',
        userName: '',
        firstName: '',
        lastName: '',
        email: '',
        inactive: false,
        status: 'Active',
        roles: ['Editor']
      },
      initialUser: {
        userId: '',
        userName: '',
        firstName: '',
        lastName: '',
        email: '',
        inactive: false,
        status: 'Active',
        roles: ['Editor']
      },
      errors: {},
      errorMessages: []
    },
    states: {
      idle: {
        on: {
          INIT: {
            actions: ['setUser', 'setNotifyParentOfSave'],
            target: 'userLoaded'
          },
          INIT_ADD: {
            target: 'userLoaded',
            actions: ['resetUser', 'setNotifyParentOfSave']
          }
        }
      },
      userLoaded: {
        on: {
          MUTATE: {
            actions: ['mutate', 'setDirty'],
            target: 'userLoaded'
          },
          SAVE: {
            target: 'saving',
            actions: ['resetErrors']
          },
          SAVE_NEW_USER: {
            target: 'savingNewUser',
            actions: ['resetErrors']
          }
        }
      },
      saving: {
        invoke: {
          id: 'saveUser',
          src: 'saveUser',
          input: ({ context, event }) => ({ context, event }),
          onDone: {
            actions: ['clearErrors', 'setSuccess', 'notifyParent'],
            target: 'userLoaded'
          },
          onError: {
            actions: ['setError'],
            target: 'userLoaded'
          }
        }
      },
      savingNewUser: {
        invoke: {
          id: 'saveNewUser',
          src: 'saveNewUser',
          input: ({ context, event }) => ({ context, event }),
          onDone: {
            actions: ['clearErrors', 'setSuccess', 'notifyParent', 'resetUser'],
            target: 'userLoaded'
          },
          onError: {
            actions: ['setError'],
            target: 'userLoaded'
          }
        }
      }
    }
  },
  {
    actors: {
      saveUser: fromPromise(
        async ({ input }: { input: { context: EditUserState; event: SaveEvent } }) => {
          const { context, event } = input
          const { user } = event

          const errors: { [key: string]: string } = {}
          if (user.firstName.length < 1) {
            errors.firstName = 'First name must be provided'
          }

          if (user.lastName.length < 1) {
            errors.lastName = 'Last name must be provided'
          }

          if (Object.keys(errors).length > 0) {
            throw new Error('validation')
          }

          const rolesToRemove =
            context?.initialUser?.roles?.filter(role => !user?.roles?.includes(role)) || []
          const rolesToAdd =
            user?.roles?.filter(role => !context?.initialUser?.roles?.includes(role)) || []

          const userToSave: ApiEditUser = {
            userName: user.userName,
            firstName: user.firstName,
            lastName: user.lastName,
            rolesToAdd: rolesToAdd?.length > 0 ? rolesToAdd : undefined,
            rolesToRemove: rolesToRemove?.length > 0 ? rolesToRemove : undefined,
            portfolioManagerCategory: user.portfolioManagerCategory
          }
          try {
            const response = await Api.usermanager.usermanagerUpdate(userToSave)
            const { data } = response
            return data
          } catch (error) {
            throw {
              error: 'Failed to create user'
            }
          }
        }
      ),
      saveNewUser: fromPromise(
        async ({ input }: { input: { context: EditUserState; event: SaveNewUserEvent } }) => {
          const { event } = input
          const { user } = event

          const errors: { [key: string]: string } = {}
          if (user.firstName.length < 1) {
            errors.firstName = 'First name must be provided'
          }

          if (user.lastName.length < 1) {
            errors.lastName = 'Last name must be provided'
          }

          if (user.email.length < 1 || (user.email && !user.email.match(EMAIL_REGEX))) {
            errors.email = 'Email must be valid'
          }

          if (Object.keys(errors).length > 0) {
            throw {
              type: 'validation',
              errors
            }
          }

          if (event.userType === 'business-unit') {
            const userToSave: ApiAddBusinessUnitUser = {
              firstName: user.firstName,
              lastName: user.lastName,
              email: user.email,
              roles: user.roles,
              portfolioManagerCategory: user.portfolioManagerCategory
            }

            const { businessUnitId } = event

            try {
              const response = await Api.tenant.usersCreate(businessUnitId, TENANT_ID, userToSave)
              const { data } = response
              return data
            } catch (error) {
              throw {
                error: 'Failed to create user'
              }
            }
          }
          const userToSave: ApiAddClientUser = {
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email
          }

          const { clientId } = event
          try {
            const response = await Api.tenant.usersCreate2(clientId, TENANT_ID, userToSave)
            const { data } = response
            return data
          } catch (error) {
            throw {
              error: 'Failed to create user'
            }
          }
        }
      )
    },
    actions: {
      notifyParent: assign(({ context }) => {
        context.notifyParentOfSave?.()
        return {}
      }),
      setUser: assign(({ context, event }: any) => {
        const initialUser = JSON.parse(JSON.stringify(event.user))
        return {
          ...context,
          user: event.user,
          initialUser
        }
      }),
      setNotifyParentOfSave: assign(({ event }: any) => ({
        notifyParentOfSave: event.notifyParentOfSave
      })),
      resetUser: assign(context => ({
        ...context,
        user: {
          userId: '',
          userName: '',
          firstName: '',
          lastName: '',
          email: '',
          inactive: false,
          status: 'Active',
          roles: ['Editor']
        },
        initialUseruser: {
          userId: '',
          userName: '',
          firstName: '',
          lastName: '',
          email: '',
          inactive: false,
          status: 'Active',
          roles: ['Editor']
        }
      })),
      mutate: assign(({ context, event }: any) => {
        const user = { ...context.user }
        event.mutation(user)

        return {
          ...context,
          user
        }
      }),
      setDirty: assign(context => ({
        ...context,
        isSaveEnabled: true
      })),
      resetErrors: assign(context => ({
        ...context,
        errors: {}
      })),
      setError: assign(({ context, event }: any) => setErrorsAction(context, event)),
      clearErrors: assign(context => ({
        ...context,
        errorMessages: [],
        errors: {}
      })),
      setSuccess: assign(context => ({
        ...context,
        isSaveSuccess: true
      }))
    }
  }
)
